import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { EMAIL_EINSTELLUNGEN_ADMIN_PATH } from '@mcikm-vote/shared';
import type { EmailEinstellungenAntwort, EmailEinstellungenEingabe } from '@mcikm-vote/shared';

import { apiAnfrage } from '../api/anfrage';
import type { ApiFehler } from '../api/anfrage';

/**
 * Verwaltung der E-Mail-Einstellungen (ÄA-7).
 *
 * Wie bei der Personalisierung (ÄA-5) gibt es hier **keinen** öffentlichen Zwischenspeicher zu
 * entwerten — diese Angaben sind nur im Administrationsbereich relevant.
 */

export const EMAIL_EINSTELLUNGEN_SCHLUESSEL = ['admin', 'email-einstellungen'] as const;

export function useEmailEinstellungen(): UseQueryResult<EmailEinstellungenAntwort, ApiFehler> {
  return useQuery<EmailEinstellungenAntwort, ApiFehler>({
    queryKey: EMAIL_EINSTELLUNGEN_SCHLUESSEL,
    queryFn: () => apiAnfrage<EmailEinstellungenAntwort>(EMAIL_EINSTELLUNGEN_ADMIN_PATH),
  });
}

export function useEmailEinstellungenSpeichern(): UseMutationResult<
  EmailEinstellungenAntwort,
  ApiFehler,
  EmailEinstellungenEingabe
> {
  const queryClient = useQueryClient();

  return useMutation<EmailEinstellungenAntwort, ApiFehler, EmailEinstellungenEingabe>({
    mutationFn: (eingabe) =>
      apiAnfrage<EmailEinstellungenAntwort>(EMAIL_EINSTELLUNGEN_ADMIN_PATH, {
        methode: 'PUT',
        koerper: eingabe,
      }),
    onSuccess: (antwort) => {
      queryClient.setQueryData(EMAIL_EINSTELLUNGEN_SCHLUESSEL, antwort);
    },
  });
}

/** Testversand an eine einmalig eingegebene Adresse — der einzige Weg, die `.env` zu prüfen. */
export function useEmailTestversand(): UseMutationResult<void, ApiFehler, string> {
  return useMutation<void, ApiFehler, string>({
    mutationFn: (adresse) =>
      apiAnfrage<void>(`${EMAIL_EINSTELLUNGEN_ADMIN_PATH}/test`, {
        methode: 'POST',
        koerper: { adresse },
      }),
  });
}
